import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalFooter } from "@/components/LegalFooter";
import { InstallAppButton } from "@/components/InstallAppButton";
import { AuthLinkLanding } from "@/components/AuthLinkLanding";
import { COMPANY_NAME, COMPANY_URL, MARKETING_TAGLINE, MARKETING_SUBHEAD } from "@/lib/brand";





export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: `${COMPANY_NAME} — ${MARKETING_TAGLINE}` },
      { name: "description", content: `${MARKETING_TAGLINE} Join bartenders and venues working directly, on their own terms.` },
      { property: "og:title", content: `${COMPANY_NAME} — ${MARKETING_TAGLINE}` },
      { property: "og:description", content: `${MARKETING_TAGLINE} Join bartenders and venues working directly, on their own terms.` },
      { property: "og:url", content: `${COMPANY_URL}/` },
    ],
    links: [{ rel: "canonical", href: `${COMPANY_URL}/` }],
  }),
  component: Landing,
});

function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <AuthLinkLanding />
      <header className="flex items-center justify-between gap-4 px-4 py-5 sm:px-6 lg:px-16">


        <div className="min-w-0">
          <p className="truncate tracking-wide-luxury text-accent text-xs sm:text-sm md:text-base lg:text-xl xl:text-2xl">
            The Hospitality
          </p>
          <p className="truncate font-display text-2xl text-foreground sm:text-3xl md:text-3xl lg:text-4xl">
            Reserve
          </p>
        </div>
        <Link
          to="/auth"
          className="shrink-0 border border-accent px-4 py-2.5 text-xs tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors sm:px-6 sm:py-2"
        >
          <span className="sm:hidden">Sign In</span>
          <span className="hidden sm:inline">Sign in</span>
        </Link>
      </header>

      <main>
        {/* Split-screen hero */}
        <section className="grid min-h-[auto] grid-cols-1 lg:min-h-[calc(100vh-6rem)] lg:grid-cols-2">
          {/* Left: copy on white */}
          <div className="flex min-h-[60vh] flex-col justify-center px-4 py-14 sm:min-h-0 sm:px-6 sm:py-20 lg:px-16 lg:py-20">
            <p className="tracking-wide-luxury text-accent">Apply now</p>
            <span className="gold-divider mt-5" />
            <h1 className="mt-5 text-balance font-display text-[2.5rem] leading-[1.05] text-primary sm:text-5xl md:text-6xl">
              {MARKETING_TAGLINE}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:mt-8 sm:text-lg">
              {MARKETING_SUBHEAD}
            </p>
            <div className="mt-8 flex w-full flex-col gap-3 sm:mt-10 sm:w-auto sm:flex-row sm:flex-wrap sm:gap-4">
              <Link
                to="/auth"
                search={{ mode: "signup", role: "bartender" } as never}
                className="w-full bg-primary px-6 py-4 text-center text-xs tracking-wide-luxury text-primary-foreground hover:bg-primary/90 transition-colors sm:w-auto sm:px-8"
              >
                Apply now
              </Link>
              <Link
                to="/auth"
                search={{ mode: "signin" } as never}
                className="w-full border border-border px-6 py-4 text-center text-xs tracking-wide-luxury text-foreground hover:border-accent hover:text-accent transition-colors sm:w-auto sm:px-8"
              >
                Sign in
              </Link>
              <div className="md:hidden">
                <InstallAppButton
                  label="Add to Home Screen"
                  className="inline-flex w-full items-center justify-center gap-2 border border-accent px-6 py-4 text-xs tracking-wide-luxury text-accent hover:bg-accent hover:text-accent-foreground transition-colors sm:w-auto sm:px-8"
                />
              </div>
              {/* Desktop: tasteful entry, only when the browser actually supports install */}
              <div className="hidden md:inline-flex">
                <InstallAppButton
                  requirePrompt
                  label="Install desktop app"
                  className="inline-flex items-center gap-2 border border-border px-6 py-4 text-xs tracking-wide-luxury text-muted-foreground hover:border-accent hover:text-accent transition-colors sm:px-8"
                />
              </div>
            </div>
          </div>

          {/* Right: tranquil blue panel */}
          <div className="relative flex min-h-[55vh] items-center justify-center overflow-hidden bg-secondary px-4 py-16 sm:min-h-0 sm:px-6 sm:py-20 lg:min-h-0 lg:px-16 lg:py-20">
            <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary to-accent/20" />
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />

            <blockquote className="relative max-w-sm text-center sm:max-w-md">
              <p className="font-display text-3xl leading-tight text-primary sm:text-4xl md:text-5xl">
                Flexible work. Frictionless staffing.
              </p>
              <p className="mt-10 tracking-wide-luxury text-accent">— {COMPANY_NAME}</p>
            </blockquote>
          </div>
        </section>

        {/* Pillars */}
        <section className="border-t border-border bg-background px-4 py-16 sm:px-6 lg:px-16 lg:py-20">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-center font-display text-3xl text-foreground md:text-4xl mb-12">
              What we offer
            </h2>
            <ol className="grid list-none gap-12 sm:grid-cols-2">
              {[
                { k: "01", t: "Considered Onboarding", d: "We’re building a community of capable professionals. Our onboarding simply requires a minimum of 6 months bar experience and a quick digital Right to Work check." },
                { k: "02", t: "Your Professional Profile", d: "Showcase your experience, skills, and background on a clean profile that venues can invite directly to their shifts." },
                { k: "03", t: "Direct Bookings", d: "Venues book you directly through the app. You choose your hours, accept the work you want, and get paid straight to your bank account." },
                { k: "04", t: "Free Direct Hires", d: "Treat every shift like a working interview. If a bartender crushes a Saturday night and you want them on your permanent rota, hire them directly off the app. No agency transfer fees, no buyouts — just great local talent." },
              ].map((b) => (
                <li key={b.k}>
                  <p aria-hidden="true" className="tracking-wide-luxury text-accent">{b.k}</p>
                  <h3 className="mt-4 font-display text-2xl text-foreground">{b.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-secondary/30 px-4 py-16 sm:px-6 lg:px-16 lg:py-24">
          <div className="mx-auto max-w-3xl">
            <p className="tracking-wide-luxury text-accent text-center">Frequently asked</p>
            <span className="gold-divider mt-5 mx-auto" />
            <h2 className="mt-6 text-center font-display text-3xl text-foreground md:text-4xl">
              Eligibility, verification &amp; how it works
            </h2>
            <p className="mt-4 text-center text-sm leading-relaxed text-muted-foreground">
              Everything you need to know before you apply to The Hospitality Reserve.
            </p>

            <div className="mt-10 space-y-3">
              {[
                {
                  q: "Who can join as a bartender?",
                  a: "Professionals with a minimum of six months' paid bar experience and the right to work in the UK. We look for reliable people who take pride in their craft — not a specific CV.",
                },
                {
                  q: "Who can post shifts as a venue?",
                  a: "Licensed bars, restaurants, hotels, event caterers, and private-event organisers based in the UK. During onboarding you'll confirm your trading details and a point of contact so bartenders can trust who they're working with.",
                },
                {
                  q: "How does verification work?",
                  a: "Bartenders upload their CV so we can confirm their bar experience, then complete a digital Right to Work check via Veriff. Venues confirm their business details. We keep the review light-touch but firm — most applicants are cleared within one working day.",
                },
                {
                  q: "How does shift matching work?",
                  a: "Venues post a shift with the role, times, and rate. Bartenders within their chosen travel radius receive real-time alerts.",
                },
                {
                  q: "How and when do bartenders get paid?",
                  a: "Bartenders keep 100% of their agreed hourly rate. Payouts reach your bank account within 3–5 business days of a completed shift — Stripe needs a little time to process the transfer. Venues are charged a simple flat platform fee on top.",
                },
                {
                  q: "What does it cost to post a shift?",
                  a: "There's no subscription. Venues pay a flat £5 platform fee per booked staff member — always shown up-front on the price bar before you post.",
                },
                {
                  q: "What happens after I apply?",
                  a: "You'll be sent an email to confirm your address, and then taken to your Application Status page where you can track each verification step and see exactly what's needed next.",
                },
              ].map((item) => (
                <details
                  key={item.q}
                  className="group border border-border bg-background px-5 py-4 transition-colors open:border-accent"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-medium text-foreground">
                    <span>{item.q}</span>
                    <span className="text-accent transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                  </summary>
                  {(Array.isArray(item.a) ? item.a : [item.a]).map((para, i) => (
                    <p key={i} className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {para}
                    </p>
                  ))}
                </details>
              ))}

            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-3">
              <Link
                to="/auth"
                search={{ mode: "signup", role: "bartender" } as never}
                className="bg-primary px-8 py-4 text-xs tracking-wide-luxury text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Apply now
              </Link>
            </div>
          </div>
        </section>
      </main>

      <LegalFooter />

      <div className="px-4 py-4 text-center sm:px-6 lg:px-16">
        <Link
          to="/admin-auth"
          className="text-[10px] tracking-wide-luxury text-muted-foreground/60 hover:text-accent transition-colors"
        >
          Admin
        </Link>
      </div>
    </div>
  );
}
